import type { Artifact } from '@dev-streetbeat/chat';
import { create } from './store';
import { getPortfolioId } from 'utils/portfolioUtils';
import { ConstraintSettingGeneric } from 'types/ConstraintsTypes';
import { ConstraintsService } from 'shared/services/ConstraintsService';
import { ProductListResponseItem } from 'types/ProductListTypes';
import {
  ProductConstraints,
  ProductDisplayData,
  ProductDisplayRequestItem,
  PortfolioProductValue,
  PortfolioProductValuesMap,
} from 'types/ProductTypes';
import { createLogger } from 'shared/logger';

const log = createLogger('ActivityPanel');

type PortfolioCreationType = {
  messageId: string;
  hasCreated: boolean;
};
type ActivityPanelStoreState = {
  parsedConfigConstraintsData: ConstraintSettingGeneric[];
  isPortfolioCreated: PortfolioCreationType;
  portfolioMsgIds: string[];
  attachmentsRedDot: boolean;
  portfoliosRedDot: boolean;
  finecoPortfolioArtifacts: Record<string, any[]>; // SDK artifacts
  userSentFirstMessage: boolean;
  newAttachmentsAdded: boolean;
  newConstraintsAdded: boolean;
  openedConstraintsByThread: Record<string, string[]>;
  hasSentMessage: boolean;
  constraintsDefault: any;
  ptfThread: any;
  constraintsPanelMode: 'builder' | 'rebalancing' | 'diagnosis';
  feeType: 'feeOnTop' | 'feeOnly' | null;
  productLists: ProductListResponseItem[];
  // Microcategory id → display name. Populated by `fetchMicrocategoryNames`
  // on activity-panel mount; consumed by `MicroCategoryContent` (so the user
  // sees names instead of numeric ids in the panel) and by
  // `buildVincoliRecap` (so the Invia recap renders names too).
  microcategoryNames: Record<string, string>;
  // Product display data cache (fetched on-demand)
  productDisplayCache: Record<string, ProductDisplayData>;
  // ISINs whose display data is currently being fetched
  pendingDisplayIsins: Record<string, true>;
  // Portfolio product values from metadata (synced from threadMetadata['portfolio_product_values'])
  portfolioProductValues: PortfolioProductValuesMap;
  // Product Modal state (rendered at top level to survive responsive changes)
  productModalOpen: boolean;
  productModalData: ProductConstraints | null;
  productModalOnSave: ((data: ProductConstraints) => void) | null;
};

type ActivityPanelStoreActions = {
  setParsedConfigConstraintsData: (parsedConfigConstraintsData: ConstraintSettingGeneric[]) => void;
  setIsPortfolioCreated: (portfolioCreation: PortfolioCreationType) => void;
  setAttachmentsRedDot: (redDot: boolean) => void;
  setFinecoPortfolioArtifact: (threadId: string, finecoPortfolioArtifact: Artifact) => void;
  setUserSentFirstMessage: (userSentFirstMessage: boolean) => void;
  setNewAttachmentsAdded: (newAttachmentsRedDot: boolean) => void;
  setNewConstraintsAdded: (newConstraintsAdded: boolean) => void;
  setOpenedConstraints: (threadId: string, constraints: string[]) => void;
  getOpenedConstraints: (threadId: string) => string[];
  setHasSentMessage: (useHasSentMessage: boolean) => void;
  fetchConstraintsDefault: () => void;
  fetchProductLists: () => void;
  fetchMicrocategoryNames: () => Promise<void>;
  fetchPtfByPortfolioData: (portfolioData: any) => void;
  setConstraintsPanelMode: (mode: 'builder' | 'rebalancing' | 'diagnosis') => void;
  setFeeType: (feeType: 'feeOnTop' | 'feeOnly' | null) => void;
  openProductModal: (data: ProductConstraints, onSave: (data: ProductConstraints) => void) => void;
  closeProductModal: () => void;
  // Product display cache actions
  fetchProductDisplayData: (items: ProductDisplayRequestItem[]) => Promise<void>;
  setPortfolioProductValues: (values: PortfolioProductValuesMap) => void;
  getProductDisplay: (isin: string) => ProductDisplayData | undefined;
  getPortfolioValue: (isin: string) => PortfolioProductValue | undefined;
  clearDisplayCache: () => void;
  resetStore: () => void;
};

const initialData: ActivityPanelStoreState = {
  parsedConfigConstraintsData: null,
  isPortfolioCreated: { messageId: '', hasCreated: false },
  portfolioMsgIds: [],
  attachmentsRedDot: false,
  portfoliosRedDot: false,
  finecoPortfolioArtifacts: {},
  userSentFirstMessage: false,
  newAttachmentsAdded: false,
  newConstraintsAdded: false,
  openedConstraintsByThread: {},
  hasSentMessage: false,
  constraintsDefault: null,
  ptfThread: null,
  constraintsPanelMode: 'builder',
  feeType: null,
  productLists: [],
  microcategoryNames: {},
  productDisplayCache: {},
  pendingDisplayIsins: {},
  portfolioProductValues: {},
  productModalOpen: false,
  productModalData: null,
  productModalOnSave: null,
};

const activityPanelStore = create<ActivityPanelStoreState & ActivityPanelStoreActions>()((set, get) => ({
  ...initialData,
  setParsedConfigConstraintsData: (parsedConfigConstraintsData: ConstraintSettingGeneric[]) => {
    set({ parsedConfigConstraintsData: parsedConfigConstraintsData });
  },
  setIsPortfolioCreated: (portfolioCreation) => {
    set({
      isPortfolioCreated: portfolioCreation,
    });
  },
  setAttachmentsRedDot: (redDot) => {
    set({ attachmentsRedDot: redDot });
  },
  setFinecoPortfolioArtifact: (threadId, finecoPortfolioArtifact) => {
    const existingArtifacts = get().finecoPortfolioArtifacts[threadId] || [];

    // Extract portfolio_id using shared helper
    const portfolioId = getPortfolioId(finecoPortfolioArtifact);

    if (!portfolioId) return;

    const isAlreadyAdded = existingArtifacts.some((artifact) => {
      const existingPortfolioId = getPortfolioId(artifact);
      return existingPortfolioId === portfolioId;
    });
    if (isAlreadyAdded) return;
    set({
      finecoPortfolioArtifacts: {
        ...get().finecoPortfolioArtifacts,
        [threadId]: [...existingArtifacts, finecoPortfolioArtifact],
      },
    });
  },
  setUserSentFirstMessage: (userSentFirstMessage) => {
    set({ userSentFirstMessage: userSentFirstMessage });
  },
  setNewAttachmentsAdded: (newAttachmentsAdded) => {
    set({ newAttachmentsAdded: newAttachmentsAdded });
  },
  setNewConstraintsAdded: (newConstraintsAdded) => {
    set({ newConstraintsAdded: newConstraintsAdded });
  },

  setOpenedConstraints: (threadId, constraints) => {
    set((state) => ({
      openedConstraintsByThread: {
        ...state.openedConstraintsByThread,
        [threadId]: constraints,
      },
    }));
  },
  getOpenedConstraints: (threadId) => {
    return get().openedConstraintsByThread[threadId] || [];
  },
  setHasSentMessage: (hasSentMessage) => {
    set({ hasSentMessage: hasSentMessage });
  },
  fetchConstraintsDefault: async () => {
    try {
      const constraintsDefaultResults = await ConstraintsService.fetchConstraintsDefault();
      set({
        constraintsDefault: constraintsDefaultResults,
      });
    } catch (error) {
      log.error('fetch constraints default failed', { error });
    }
  },
  fetchProductLists: async () => {
    try {
      const productListsResults = await ConstraintsService.getProductLists();
      set({
        productLists: productListsResults,
      });
    } catch (error) {
      log.error('fetch product lists failed', { error });
    }
  },
  fetchMicrocategoryNames: async () => {
    try {
      const names = await ConstraintsService.getMicrocategoryNames();
      set({ microcategoryNames: names });
    } catch (error) {
      log.error('fetch microcategory names failed', { error });
    }
  },
  fetchPtfByPortfolioData: async (portfolioData) => {
    const ptfResults = await ConstraintsService.fetchPtf(portfolioData.portfolio_id);
    set({
      ptfThread: ptfResults,
    });
  },
  setConstraintsPanelMode: (mode) => {
    set({ constraintsPanelMode: mode });
  },
  setFeeType: (feeType) => {
    set({ feeType });
  },
  openProductModal: (data, onSave) => {
    set({
      productModalOpen: true,
      productModalData: data,
      productModalOnSave: onSave,
    });
  },
  closeProductModal: () => {
    set({
      productModalOpen: false,
      productModalData: null,
      productModalOnSave: null,
    });
  },
  fetchProductDisplayData: async (items: ProductDisplayRequestItem[]) => {
    if (items.length === 0) return;

    // Filter out items whose ISIN we already have cached
    const cachedIsins = get().productDisplayCache;
    const missingItems = items.filter((item) => !cachedIsins[item.isin]);

    if (missingItems.length === 0) return;

    const missingIsins = missingItems.map((item) => item.isin);
    set((state) => {
      const updated = { ...state.pendingDisplayIsins };
      for (const isin of missingIsins) updated[isin] = true;
      return { pendingDisplayIsins: updated };
    });

    try {
      const displayData = await ConstraintsService.getProductDisplayData(missingItems);
      const newCache = { ...get().productDisplayCache };
      for (const item of displayData) {
        newCache[item.isin] = item;
      }
      set({ productDisplayCache: newCache });
    } catch (error) {
      log.error('fetch product display data failed', { error });
    } finally {
      set((state) => {
        const updated = { ...state.pendingDisplayIsins };
        for (const isin of missingIsins) delete updated[isin];
        return { pendingDisplayIsins: updated };
      });
    }
  },
  setPortfolioProductValues: (values: PortfolioProductValuesMap) => {
    set({ portfolioProductValues: values });
  },
  getProductDisplay: (isin: string) => {
    return get().productDisplayCache[isin];
  },
  getPortfolioValue: (isin: string) => {
    return get().portfolioProductValues[isin];
  },
  clearDisplayCache: () => {
    set({
      productDisplayCache: {},
      pendingDisplayIsins: {},
      portfolioProductValues: {},
    });
  },
  resetStore: () => {
    const currentConstraintsDefault = get().constraintsDefault;
    const currentConstraintsPanelMode = get().constraintsPanelMode;
    const currentProductLists = get().productLists;
    // Thread-independent reference data, fetched once on ActivityPanelContainer
    // mount: wiping it on thread switch left microcategory rows on
    // "caricamento..." forever (no refetch path exists after mount).
    const currentMicrocategoryNames = get().microcategoryNames;

    // feeType is intentionally NOT preserved: it is re-derived per-thread from the
    // imported-portfolio metadata in useConstraintsMetadata. Preserving it let a
    // prior portfolio's regime stick across a thread switch (Fee Only shown as Fee
    // on Top). Both resetStore callers navigate to another thread, so the
    // per-thread effect restores the correct value immediately.
    set({
      ...initialData,
      constraintsDefault: currentConstraintsDefault,
      constraintsPanelMode: currentConstraintsPanelMode,
      productLists: currentProductLists,
      microcategoryNames: currentMicrocategoryNames,
    });
  },
}));

export default activityPanelStore;
